import moment from 'moment';
import type { Dispatch, SetStateAction } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import type { SetNonNullable } from 'type-fest';
import { useQuill } from '../../editor/QuillContext';
import type { MagneticWallHighlight } from '../../editor/editorSlice';
import { getClosestAttribute } from '../../editor/formats/utils/attributors';
import useSyncMagneticWallStates from '../../editor/useSyncMagneticWallState';
import type { DocumentType } from '../../graphql/generated';
import { gql } from '../../graphql/generated';
import ClickOutsideHandler from '../../utils/ClickOutsideHandler';
import {
  PostLayoutSize,
  usePostLayoutSize,
} from '../../utils/responsive/PostLayoutSizeProvider';
import annotationThreadHash from '../../utils/url/annotationThreadHash';
import commentThreadHash from '../../utils/url/commentThreadHash';
import type { MagneticWallItemSize } from '../MagneticWallItem';
import MagneticWallLayout, {
  useMagneticWallLayout,
} from '../MagneticWallLayout';
import useMagneticWallItemSize from '../useMagneticWallItemSize';
import parseMagneticWallHash from '../utils/parseMagneticWallHash';
import type { AnnotationVersionChange } from './AnnotationVersionChangeCard';
import AnnotationVersionChangeCard from './AnnotationVersionChangeCard';
import type { ThreadVersionChange } from './ThreadVersionChangeCard';
import ThreadVersionChangeCard from './ThreadVersionChangeCard';
import {
  getContentRectsForMagneticWallVersionChanges,
  useVersionChangeMagneticWallFocus,
  useVersionChanges,
  useVersionChangesFocus,
} from './VersionChanges';

const THREAD_FRAGMENT = gql(`
  fragment PostHistoryMagneticWall_thread on CommentThread {
    id
    ...ThreadVersionChangeCard_thread
  }
`);

const ANNOTATION_FRAGMENT = gql(`
  fragment PostHistoryMagneticWall_annotation on Annotation {
    id
    ...AnnotationVersionChangeCard_annotation
  }
`);

export type PostHistoryMagneticWallVersionChanges =
  | ThreadVersionChange<DocumentType<typeof THREAD_FRAGMENT>>
  | AnnotationVersionChange<DocumentType<typeof ANNOTATION_FRAGMENT>>;

const copyLink = (hash: string) => {
  const link = new URL(window.location.href);
  link.hash = hash;
  navigator.clipboard.writeText(link.toString());
};

interface InnerPostHistoryMagneticWallProps {
  versionChanges: PostHistoryMagneticWallVersionChanges[];
  magneticWallItemSize: MagneticWallItemSize;
  canEdit: boolean;
  magneticWallHighlight: MagneticWallHighlight | null;
  setMagneticWallHighlight: Dispatch<
    SetStateAction<MagneticWallHighlight | null>
  >;
}

const InnerPostHistoryMagneticWall = ({
  versionChanges,
  magneticWallItemSize,
  canEdit,
  magneticWallHighlight,
  setMagneticWallHighlight,
}: InnerPostHistoryMagneticWallProps) => {
  const { quill } = useQuill();
  const { layout, debouncedUpdateDesiredPositions, updateThreadHeights } =
    useMagneticWallLayout();
  const { focusedVersionChange } = useVersionChangesFocus();
  const {
    focusMagneticWallItem,
    blurMagneticWallFocus,
    magneticWallChangeFocus,
  } = useVersionChangeMagneticWallFocus();
  const { updateVersionChanges } = useVersionChanges();

  const positionedItems = useMemo(
    () =>
      versionChanges
        .map((item) => {
          return {
            ...item,
            top: layout.tops[item.id],
            isOverlapped: layout.overlappedIds.has(item.id),
          };
        })
        .filter(
          (item): item is SetNonNullable<typeof item, 'top'> =>
            item.top != null,
        ),
    [layout.overlappedIds, layout.tops, versionChanges],
  );

  const hasPositionedItems = positionedItems.length > 0;
  const location = useLocation();

  useEffect(() => {
    if (!hasPositionedItems) return;

    const parseResult = parseMagneticWallHash(location.hash);
    if (!parseResult) return;

    focusMagneticWallItem(parseResult.id, parseResult.type);
  }, [hasPositionedItems, location.hash, focusMagneticWallItem]);

  useEffect(() => {
    debouncedUpdateDesiredPositions();
    updateThreadHeights();

    // when the items have positioned, we need to update the version changes
    // to make sure that the order of version changes is correct
    if (quill) {
      updateVersionChanges(quill);
    }
  }, [
    positionedItems,
    debouncedUpdateDesiredPositions,
    updateThreadHeights,
    updateVersionChanges,
    quill,
  ]);

  useSyncMagneticWallStates({
    magneticWallFocus: magneticWallChangeFocus,
    magneticWallHighlight,
  });

  return (
    <ClickOutsideHandler
      onClickOutside={(e) => {
        const { target } = e;

        if (
          target instanceof Element &&
          // clicking on a comment / annotation within the post
          // shouldn't blur the magneticWallFocus, as it's selecting it
          getClosestAttribute(target) != null
        ) {
          return;
        }

        blurMagneticWallFocus();
      }}
    >
      {positionedItems.map((versionChange) => {
        const isFocused =
          versionChange.id === magneticWallChangeFocus?.id ||
          versionChange.id === focusedVersionChange?.id;

        const onFocus = () => {
          focusMagneticWallItem(versionChange.id, versionChange.type);
        };

        const onHighlight = () => {
          setMagneticWallHighlight({
            id: versionChange.id,
            type: versionChange.type,
          });
        };

        if (versionChange.type === 'annotation') {
          return (
            <MagneticWallLayout.Item
              id={versionChange.id}
              focused={isFocused}
              top={versionChange.top}
              key={versionChange.id}
            >
              <AnnotationVersionChangeCard
                focused={isFocused}
                shouldOpenPopOver={
                  (isFocused && magneticWallChangeFocus?.active) ?? false
                }
                versionChange={versionChange}
                onFocus={onFocus}
                onCopyLink={() =>
                  copyLink(annotationThreadHash(versionChange.id))
                }
                onRemoveHighlight={() => setMagneticWallHighlight(null)}
                onHighlight={onHighlight}
                magneticWallItemSize={magneticWallItemSize}
              />
            </MagneticWallLayout.Item>
          );
        }

        return (
          <MagneticWallLayout.Item
            id={versionChange.id}
            focused={isFocused}
            top={versionChange.top}
            key={versionChange.id}
          >
            <ThreadVersionChangeCard
              focused={isFocused}
              shouldOpenPopOver={
                (isFocused && magneticWallChangeFocus?.active) ?? false
              }
              versionChange={versionChange}
              magneticWallItemSize={magneticWallItemSize}
              canEdit={canEdit}
              onFocus={onFocus}
              onCopyLink={() => copyLink(commentThreadHash(versionChange.id))}
              onRemoveHighlight={() => setMagneticWallHighlight(null)}
              onHighlight={onHighlight}
            />
          </MagneticWallLayout.Item>
        );
      })}
    </ClickOutsideHandler>
  );
};

interface PostHistoryMagneticWallProps {
  versionChanges: PostHistoryMagneticWallVersionChanges[];
  canEdit: boolean;
  magneticWallHighlight: MagneticWallHighlight | null;
  setMagneticWallHighlight: Dispatch<
    SetStateAction<MagneticWallHighlight | null>
  >;
}

const PostHistoryMagneticWall = ({
  versionChanges,
  canEdit,
  magneticWallHighlight,
  setMagneticWallHighlight,
}: PostHistoryMagneticWallProps) => {
  const postLayoutSize = usePostLayoutSize();
  const { quill } = useQuill();
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const magneticWallItemSize = useMagneticWallItemSize(container);
  const { magneticWallChangeFocus } = useVersionChangeMagneticWallFocus();

  if (
    postLayoutSize &&
    [PostLayoutSize.mobile, PostLayoutSize.tablet].includes(postLayoutSize)
  ) {
    return null;
  }

  return (
    <MagneticWallLayout.Root
      containerRef={setContainer}
      itemSize={magneticWallItemSize}
      magneticWallItems={versionChanges.toSorted((a, b) => {
        // This will make sure that resolved threads without context
        // are sorted by their resolvedAt date.
        if (
          a.type === 'comment' &&
          b.type === 'comment' &&
          a.thread.resolvedAt &&
          b.thread.resolvedAt
        ) {
          return moment(a.thread.resolvedAt).diff(moment(b.thread.resolvedAt));
        }

        return 0;
      })}
      focus={magneticWallChangeFocus}
      getRelativeContentRects={() => {
        if (!quill) return [];

        return getContentRectsForMagneticWallVersionChanges(
          versionChanges,
          quill,
        );
      }}
    >
      <InnerPostHistoryMagneticWall
        versionChanges={versionChanges}
        magneticWallItemSize={magneticWallItemSize}
        canEdit={canEdit}
        magneticWallHighlight={magneticWallHighlight}
        setMagneticWallHighlight={setMagneticWallHighlight}
      />
    </MagneticWallLayout.Root>
  );
};

export default PostHistoryMagneticWall;
